
import { Injectable } from '@angular/core';
import { GoogleGenAI, Type } from "@google/genai";
import { ENV } from '../config/env';

@Injectable({
  providedIn: 'root'
})
export class GamificationService {
  private ai: GoogleGenAI | null = null;

  private apiKey: string = '';

  constructor() {
    this.apiKey = this.loadApiKey();
    
    if (this.apiKey) {
      this.ai = new GoogleGenAI({ apiKey: this.apiKey });
      const masked = this.apiKey.length > 14 
        ? `${this.apiKey.substring(0, 10)}...${this.apiKey.substring(this.apiKey.length - 4)}`
        : '***';
      console.log(`✅ API key loaded: ${masked}`);
    } else {
      console.warn('⚠️ API key not found. Using fallback simulation.');
    }
  }

  private loadApiKey(): string {
    // Try ENV config first (consolidated from all sources)
    if (ENV.VITE_API_KEY) {
      return ENV.VITE_API_KEY;
    }

    // Fallback: Try all sources directly
    if (typeof window !== 'undefined') {
      const win = window as any;
      let key = win['__ENV__']?.VITE_API_KEY 
        || win['__VITE_API_KEY__']
        || win['VITE_API_KEY'];
      
      if (key) {
        try {
          localStorage.setItem('VITE_API_KEY', key);
        } catch (e) {
          // localStorage not available
        }
        return key;
      }
    }

    // Try import.meta.env (from Vite)
    try {
      const metaEnv = (import.meta as any)?.env;
      if (metaEnv?.VITE_API_KEY) {
        return metaEnv.VITE_API_KEY;
      }
    } catch (e) {
      // import.meta not available
    }

    return '';
  }

  async simulateTurn(
    currentDay: number,
    currentMoney: number,
    reputation: number,
    inputs: any
  ): Promise<any> {
    if (!this.apiKey || !this.ai) {
      return this.getFallbackResponse(inputs.investment);
    }

    const prompt = `
      Act as a strict business and gamification simulation engine for a coffee shop named "Pasu Coffee".
      
      Game Context:
      - Shop Name: Pasu Coffee
      - Week: ${currentDay / 7}
      - Shop Reputation: ${reputation}/10
      - Remaining Budget: ${currentMoney} THB
      
      Player Strategy (Total Investment: ${inputs.investment} THB):
      1. Goal: ${inputs.goal}
      2. Rules: ${inputs.rules}
      3. Conflict: ${inputs.conflict}
      4. Time: ${inputs.time}
      5. Reward: ${inputs.reward}
      6. Feedback: ${inputs.feedback}
      7. Levels: ${inputs.levels}

      Task:
      Simulate the business outcome for 1 week.
      Logic:
      - High investment usually brings more customers, but not always (e.g., spending 3000 on "Leaderboard" for a small shop might be waste).
      - Good gamification synergy (e.g., clear Goal + desirable Reward) increases revenue.
      - Bad synergy (e.g., hard Rules + low Reward) decreases revenue and reputation.
      - Revenue should typically range between 2,000 - 20,000 THB depending on effectiveness and investment.
      
      Return JSON:
      {
        "revenue": number (integer, gross income from sales),
        "newCustomers": number (integer),
        "reputationChange": number (integer, -1 to +2),
        "customerReviews": ["review1", "review2", "review3"],
        "educationalAnalysis": "Explain in Thai: Did the ${inputs.investment} THB investment pay off? Why? Critique the gamification logic."
      }
    `;

    try {
      const response = await this.ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              revenue: { type: Type.INTEGER },
              newCustomers: { type: Type.INTEGER },
              reputationChange: { type: Type.INTEGER },
              customerReviews: { 
                type: Type.ARRAY, 
                items: { type: Type.STRING } 
              },
              educationalAnalysis: { type: Type.STRING }
            }
          }
        }
      });

      // Handle response - check for text property or candidates
      let responseText = '';
      if (response.text) {
        responseText = response.text;
      } else if (response.candidates && response.candidates[0] && response.candidates[0].content) {
        // Fallback: try to get text from candidates
        const content = response.candidates[0].content;
        if (typeof content === 'string') {
          responseText = content;
        } else if (content.parts && content.parts[0] && content.parts[0].text) {
          responseText = content.parts[0].text;
        }
      }

      if (responseText) {
        try {
          return JSON.parse(responseText);
        } catch (parseError) {
          console.error("JSON Parse Error:", parseError, "Response:", responseText);
          throw new Error("Failed to parse AI response as JSON");
        }
      }
      
      throw new Error("No response text found in AI response");
    } catch (error: any) {
      console.error("GenAI Error:", error);
      console.error("Error details:", {
        message: error?.message,
        stack: error?.stack,
        response: error?.response
      });
      
      return this.getFallbackResponse(inputs.investment, error?.message);
    }
  }

  private getFallbackResponse(investment: number, errorMessage?: string): any {
    return {
      revenue: investment + 2000,
      newCustomers: 5,
      reputationChange: 0,
      customerReviews: [
        errorMessage 
          ? `ระบบ AI ขัดข้อง: ${errorMessage}`
          : "ระบบ AI ขัดข้อง: API key is missing. Please provide a valid API key.",
        "แต่ Pasu Coffee ยังโอเค",
        "เฉยๆ นะสัปดาห์นี้"
      ],
      educationalAnalysis: `ระบบ AI ขัดข้อง: ${errorMessage || 'API key is missing'}. จึงคืนทุนให้คุณพร้อมกำไรเล็กน้อย (${investment + 2000} THB)`
    };
  }
}
